import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { Static } from '@sinclair/typebox'

import { useBackendClient } from '../contexts/BackendClientContext'
import {
  PositionsGetInfoBatchResponseSchema,
  PositionsGetInfoBatchResponseType,
} from '../../backend/types'

export type PositionInfo = Static<typeof PositionsGetInfoBatchResponseSchema>[number]

const CACHE_KEY = 'position-details'

export const useUserPositionDetailsBatch = (positionIds: string[]) => {
  const backendClient = useBackendClient()
  const queryClient = useQueryClient()

  const batchResults = useQuery({
    queryKey: [CACHE_KEY, positionIds],
    enabled: positionIds.length > 0,
    queryFn: async () => {
      const missingPositionIds = positionIds.filter(
        id => !queryClient.getQueryData([CACHE_KEY, id])
      )
      if (missingPositionIds.length > 0) {
        const positionDetails = await backendClient.positionsGetInfoBatch({
          positionIds: missingPositionIds,
        })

        positionDetails?.forEach(p => {
          queryClient.setQueryData([CACHE_KEY, p.positionId], p)
        })
      }

      return positionIds.map(
        id =>
          queryClient.getQueryData([CACHE_KEY, id]) as
            | PositionsGetInfoBatchResponseType[number]
            | undefined
      )
    },
    refetchInterval: 30 * 1000,
  })

  const positionDetailsMap: Map<string, PositionsGetInfoBatchResponseType[number]> = (
    batchResults.data || []
  ).reduce((acc, curr) => {
    if (!curr) {
      return acc
    }
    acc.set(curr.positionId, curr)
    return acc
  }, new Map())

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  const refetchPositionDetails = useCallback(async () => {
    await sleep(1000)

    positionIds.forEach(positionId =>
      queryClient.removeQueries({
        queryKey: [CACHE_KEY, positionId],
      })
    )

    queryClient.removeQueries({
      queryKey: [CACHE_KEY, positionIds],
    })

    await batchResults.refetch()
  }, [queryClient, positionIds])

  return {
    positionDetailsMap,
    isLoading: batchResults.isLoading,
    refetch: refetchPositionDetails,
  }
}

export const useUserPositionDetails = (positionId: string) => {
  const positionDetails = useUserPositionDetailsBatch(positionId ? [positionId] : [])
  return {
    isLoading: positionDetails.isLoading,
    positionDetails: positionDetails.positionDetailsMap.get(positionId),
    refetch: positionDetails.refetch,
  }
}
